import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { VendorPerformanceDetails, VendorRiskKey } from '@/types/domain/vendor-management';
import { ShieldAlert, ShieldCheck, Star } from 'lucide-react';

interface VendorPerformanceCardProps {
  performance: VendorPerformanceDetails;
  riskRating: VendorRiskKey;
}

export const VendorPerformanceCard: React.FC<VendorPerformanceCardProps> = ({ performance, riskRating }) => {
  return (
    <Card className="border-border rounded-2xl shadow-2xs font-heading">
      <CardHeader className="pb-3 border-b border-border/60">
        <CardTitle className="font-heading text-xs font-bold text-foreground uppercase tracking-wider flex items-center justify-between">
          <span>Supplier Performance & Risk Metrics</span>
          <span
            className={`text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full flex items-center gap-1 ${
              riskRating === 'LOW'
                ? 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/20'
                : 'bg-rose-500/10 text-rose-700 border border-rose-500/20'
            }`}
          >
            {riskRating === 'LOW' ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}
            Risk: {riskRating}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Performance Score */}
        <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 flex items-center justify-between">
          <div className="space-y-0.5">
            <span className="text-[11px] font-bold text-foreground-muted uppercase">Overall Performance Index</span>
            <div className="flex items-center gap-1.5 text-2xl font-extrabold text-primary">
              <Star className="h-6 w-6 fill-current text-amber-500" />
              <span>{performance.performanceScore} / 100</span>
            </div>
          </div>

          <div className="text-right space-y-0.5">
            <span className="text-[10px] font-bold text-foreground-muted uppercase">Orders Completed</span>
            <span className="text-xl font-extrabold text-foreground block font-mono">{performance.ordersCompleted}</span>
          </div>
        </div>

        {/* Detailed Ratings */}
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="p-3 rounded-xl border border-border bg-card space-y-1">
            <span className="text-[10px] font-bold text-foreground-muted uppercase block">On-Time Delivery Rate</span>
            <span className="text-lg font-extrabold text-emerald-600 font-mono">{performance.onTimeDeliveryRate}%</span>
          </div>

          <div className="p-3 rounded-xl border border-border bg-card space-y-1">
            <span className="text-[10px] font-bold text-foreground-muted uppercase block">Quality Audit Index</span>
            <span className="text-lg font-extrabold text-emerald-600 font-mono">{performance.qualityIndex}%</span>
          </div>

          <div className="p-3 rounded-xl border border-border bg-card space-y-1">
            <span className="text-[10px] font-bold text-foreground-muted uppercase block">RFQs Participated</span>
            <span className="text-lg font-extrabold text-foreground font-mono">{performance.rfqsParticipated}</span>
          </div>

          <div className="p-3 rounded-xl border border-border bg-card space-y-1">
            <span className="text-[10px] font-bold text-foreground-muted uppercase block">Bids Submitted</span>
            <span className="text-lg font-extrabold text-foreground font-mono">{performance.bidsSubmitted}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
